// src/services/api.js
/**
 * API service for handling all external API requests
 */
const API_URL = process.env.REACT_APP_API_URL;
const CONTACT_FORM_SUBMISSION_ENDPOINT = process.env.REACT_APP_CONTACT_FORM_SUBMISSION_ENDPOINT;
const GET_SESSION_ENDPOINT = process.env.REACT_APP_ANALYTICS_GET_SESSION_ENDPOINT;
const ANALYTICS_INGEST_ENDPOINT = process.env.REACT_APP_ANALYTICS_INGEST_ENDPOINT;

/**
 * Send contact form data to backend
 * @param {Object} formData - The form data to submit
 * @returns {Promise<Object>} - The response from the server
 */
export const submitContactForm = async (formData) => {
    try {
        const response = await fetch(
            `${API_URL}${CONTACT_FORM_SUBMISSION_ENDPOINT}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            }
        );

        let data;
        try {
            data = await response.json();
        } catch (jsonErr) {
            data = {};
        }

        if (!response.ok || !data.success) {
            // Only show user-facing error messages
            const message = (data && typeof data.message === 'string')
                ? data.message
                : 'Failed to submit contact form. Please try again.';
            return {
                success: false,
                message,
                status: response.status,
            };
        }

        return {
            success: true,
            message: data.message || 'Thank you! Your message has been sent.',
            status: response.status,
        };
    } catch (error) {
        console.error('API Error:', error);
        return {
            success: false,
            message: 'An unexpected error occurred. Please try again later.',
            status: 0,
        };
    }
};

/**
 * Get a session token for the user from the get-session endpoint
 * @returns {Promise<string>} - The session token
 */
export const getSessionToken = async ({ startPage, referrer, timestamp }) => {
    if (!API_URL || !GET_SESSION_ENDPOINT) {
        throw new Error('API URL or GET_SESSION_ENDPOINT is not defined');
    }
    if (!startPage || !referrer || !timestamp) {
        throw new Error('startPage, referrer, and timestamp are required parameters');
    }
    try {
        const response = await fetch(
            `${API_URL}${GET_SESSION_ENDPOINT}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                startPage: startPage,
                referrer: referrer,
                timestamp: timestamp,
            }),
        });

        if (!response.ok) {
            throw new Error('Failed to retrieve session token');
        }

        const data = await response.json();
        return data.sessionId;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

/**
 * send an analytics event to the backend
 * @param {string} sessionToken - The session token for the user
 * @param {Object} [eventData] - Additional data to send with the event
 * @returns {Promise<void>}
 */
export const sendAnalyticsEvent = async (sessionToken, eventData) => {
    // TODO: Uncomment once the backend is ready
    // try {
    //     const response = await fetch(
    //         `${API_URL}${ANALYTICS_INGEST_ENDPOINT}`,
    //         {
    //             method: 'POST',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //             },
    //             body: JSON.stringify({
    //                 sessionToken: sessionToken,
    //                 data: eventData,
    //             }),
    //         }
    //     );

    //     if (!response.ok) {
    //         throw new Error('Failed to send analytics event');
    //     }
    // } catch (error) {
    //     console.error('API Error:', error);
    //     throw error;
    // }
};

/**
 * Load content data from the specified path
 * @param {Function} importFn - The import function to load the content
 * @returns {Promise<Object>} - The content data
 */
export const loadContent = async (importFn) => {
    try {
        return await importFn();
    } catch (error) {
        console.error('Error loading content:', error);
        throw error;
    }
};

export default {
    submitContactForm,
    loadContent,
};
